import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Play, CheckCircle } from 'lucide-react';
import { useArucoScanner } from '../../hooks/useArucoScanner';
import { LIBELLES_ANALYSE } from '../../utils/aruco';
import ScanHeader from './ScanHeader';
import ChoixTaille from './ChoixTaille';
import ScanViewport from './ScanViewport';
import DetectionBadges from './DetectionBadges';
import RepartitionReponses from './RepartitionReponses';
import BandeauCorrection from './BandeauCorrection';
import ScanFooter from './ScanFooter';
import SecoursDrawer from './SecoursDrawer';
import ReponsesSheet from './ReponsesSheet';
import PilotageFin from './PilotageFin';
import {
  ecranTelephone, revealDisponible, gesteFin, gestePrecedent, titrePilotage,
} from '../../pages/QCM/etapeSeance';
import { QCM_GREEN } from '../../utils/qcmTheme';

/**
 * Vue scan MOBILE (maquette QFlash) : le téléphone du prof devient un pur
 * outil de scan — caméra plein écran, numéro de question et compteur géant,
 * badges « ✓ Carte 7 » à la position de détection, chips des restants, la
 * répartition A-D en direct, et UN bouton. Toute la logique data/SSE/batching
 * vit dans le conteneur SessionLive ; ici on ne fait QUE remonter onScan et
 * piloter l'affichage.
 *
 * Un seul geste, toujours au même endroit (chantier jeton, 2026-09-04) :
 * « Lancer le quizz », puis « Suivant » pour chaque question, « Terminer » sur
 * la dernière carte de correction. C'est le réglage de la séance qui décide ce
 * que « Suivant » fait pendant le quizz :
 *   - correction en fin de séance : passer à la question suivante ;
 *   - correction après chaque question : d'abord révéler la bonne réponse sur
 *     la projection (automatique dès que toute la classe a répondu), puis
 *     passer à la suivante.
 * Plus de panneau de résultats : la projection montre tout à la classe, et la
 * répartition des réponses, que la projection cache avant la révélation, est
 * sous les yeux du prof pendant le scan. Le détail nominal reste au toucher
 * sur le compteur.
 *
 * `assignations` (carte → nom) vient du flux, à chaque question ; les noms y
 * sont déjà « Carte N ». Ni Retour ni Projeter : le téléphone relié n'a pas de
 * tableau de bord.
 *
 * APRÈS le quizz, l'écran suit l'ÉTAPE et non le statut (D3) : podium puis revue
 * se pilotent depuis `PilotageFin` alors que la séance est encore `en_cours`, et
 * une revue rejouée (D11) tourne sur une séance déjà `terminee`. Tous les
 * libellés et toutes les charges viennent de `etapeSeance.js`.
 */
const ScanMobile = ({
  session, statut, etape, revueOrdre, questionActive, totalQuestions, questionCourante,
  reponses, revealed, isTransitioning, erreurPilotage = '',
  scannedCodes, assignations, bonneReponse = '',
  onScan, onStart, onNextQuestion, onEtape, onReveal,
  // Le scan est-il ouvert pour la question en cours, et le geste qui l'ouvre.
  // Décidé par SessionLive (armé aussitôt, puis arbitré par le serveur qui
  // prévient la projection) : « l'étape fait foi », pas un état de l'écran.
  scanOuvert = false, onOuvrirScan,
}) => {
  const [badges, setBadges] = useState([]);
  const [flash, setFlash] = useState(0);
  const [secoursOuvert, setSecoursOuvert] = useState(false);
  const [reponsesOuvertes, setReponsesOuvertes] = useState(false);
  // Taille de l'image analysée (2026-09-22) : null = réglage automatique du
  // scanner, le cas de tout le monde ; une valeur = forcée par le prof depuis
  // le choix qu'ouvre la pastille du bandeau, jusqu'à la prochaine ouverture
  // de la page.
  const [largeurForcee, setLargeurForcee] = useState(null);
  const [choixTailleOuvert, setChoixTailleOuvert] = useState(false);
  const badgeSeq = useRef(0);
  const containerRef = useRef(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);

  const numeroQuestion = questionCourante?.question_ordre || questionActive || 1;
  const derniereQuestion = totalQuestions > 0 && questionActive >= totalQuestions;

  // Le déroulé de fin, lu une fois : quel écran, quel bouton, quelle charge.
  const ecran = ecranTelephone({ statut, etape });
  const deroule = {
    etape,
    revue_ordre: revueOrdre,
    podium_actif: session?.podium_actif,
    mode_correction: session?.mode_correction,
    totalQuestions,
  };
  const geste = gesteFin(deroule);
  const precedent = gestePrecedent(deroule);

  // Le réglage de la séance : faut-il révéler avant d'avancer ?
  const doitReveler = revealDisponible(session) && !revealed;

  // nom court affiché par code carte (prénom si dispo, sinon nom complet/équipe)
  const nomParCode = useMemo(() => {
    const m = new Map();
    for (const a of assignations) m.set(a.codeAruco, a.prenom || a.eleveNom);
    return m;
  }, [assignations]);

  // Codes ayant répondu : scans locaux (optimiste) ∪ réponses SSE (vérité back)
  const codesRepondus = useMemo(() => {
    const s = new Set(scannedCodes);
    for (const r of reponses) {
      if (r.code_aruco !== undefined && r.code_aruco !== null) s.add(r.code_aruco);
    }
    return s;
  }, [scannedCodes, reponses]);

  const restants = useMemo(
    () => assignations
      .filter((a) => !codesRepondus.has(a.codeAruco))
      .map((a) => ({ codeAruco: a.codeAruco, nom: a.prenom || a.eleveNom })),
    [assignations, codesRepondus],
  );

  const total = assignations.length;
  const done = total - restants.length;
  const complet = total > 0 && done >= total;

  // « Scan ! » (2026-09-19) : la caméra tourne mais n'analyse rien tant que
  // le prof n'a pas appuyé. Il lit la question, laisse réfléchir, appuie : la
  // projection passe alors à « Levez vos cartes » et aux prénoms en grand, et
  // on ne lit jamais une carte levée trop tôt. Fermé à chaque question par
  // construction (SessionLive porte l'ordre de la question ouverte).
  // Le scan est possible pendant le quizz, tant que la réponse n'est pas
  // révélée et que le tiroir de secours est fermé.
  const peutScanner = ecran === 'quiz' && questionActive > 0 && !secoursOuvert && !revealed;
  const scanning = peutScanner && scanOuvert;

  // Badge à la position de la carte (conversion coords vidéo → écran object-cover)
  const handleDetection = useCallback(({ code_aruco, corners, videoWidth, videoHeight }) => {
    const el = containerRef.current;
    if (!el || !videoWidth || !videoHeight) return;
    const cw = el.clientWidth;
    const ch = el.clientHeight;
    const scale = Math.max(cw / videoWidth, ch / videoHeight);
    const offX = (cw - videoWidth * scale) / 2;
    const offY = (ch - videoHeight * scale) / 2;
    const cx = (corners.reduce((s, c) => s + c.x, 0) / 4) * scale + offX;
    const cy = (corners.reduce((s, c) => s + c.y, 0) / 4) * scale + offY;
    const nom = nomParCode.get(code_aruco) || `Carte ${code_aruco}`;
    const id = ++badgeSeq.current;
    setBadges((prev) => [...prev, { id, x: cx, y: cy, nom }]);
    setTimeout(() => setBadges((prev) => prev.filter((b) => b.id !== id)), 1200);
    setFlash(Date.now());
    navigator.vibrate?.(30);
  }, [nomParCode]);

  const {
    cameraActive, cameraError, playBeep,
    torchSupported, torchOn, toggleTorch, restart, largeurAnalyse,
  } = useArucoScanner({
    videoRef,
    canvasRef,
    // La caméra s'éteint dès que le quizz est quitté : au podium comme en revue
    // la séance est encore `en_cours` (D3), et rien ne justifie de la garder
    // allumée derrière un écran de pilotage.
    active: ecran === 'quiz',
    paused: !scanning,
    scannedCodes,
    onScan,
    onDetection: handleDetection,
    largeurForcee,
  });

  // Remise à zéro au changement de question
  useEffect(() => {
    setBadges([]);
    setSecoursOuvert(false);
    setReponsesOuvertes(false);
  }, [numeroQuestion]);

  // Toute la classe a répondu : bip « complet », et la révélation part toute
  // seule si le réglage la demande. Le professeur n'a plus qu'un geste.
  // UNE fois par question (ref) : les rendus suivants ne rejouent rien, et les
  // callbacks passent par des refs pour ne pas relancer l'effet.
  const onRevealRef = useRef(onReveal);
  onRevealRef.current = onReveal;
  const playBeepRef = useRef(playBeep);
  playBeepRef.current = playBeep;
  const completSignaleRef = useRef(null);
  useEffect(() => {
    if (ecran !== 'quiz' || !complet) return;
    if (completSignaleRef.current === numeroQuestion) return;
    completSignaleRef.current = numeroQuestion;
    playBeepRef.current?.('done');
    if (doitReveler) onRevealRef.current?.();
  }, [complet, ecran, numeroQuestion, doitReveler]);

  // LE geste : révéler si le réglage le demande et que ce n'est pas fait, sinon
  // avancer ; sur la dernière question, le pas de fin (podium, revue ou fin).
  const handleSuivant = () => {
    if (doitReveler) {
      onReveal();
      return;
    }
    if (derniereQuestion) {
      if (geste) onEtape(geste.charge);
      return;
    }
    onNextQuestion();
  };
  const libelleSuivant = !doitReveler && derniereQuestion && geste ? geste.libelle : 'Suivant';

  // ── Écrans hors passation ──
  if (ecran === 'attente') {
    return (
      // Conteneur SCROLLABLE + contenu en `m-auto` : centré quand il y a la
      // place, entièrement atteignable au scroll sinon (paysage, petits
      // écrans). Le padding bas respecte la safe-area iPhone (home indicator).
      <div
        className="fixed inset-0 z-50 flex overflow-y-auto px-8 py-8 text-center text-white"
        style={{ background: '#0e1116', paddingBottom: 'max(2rem, env(safe-area-inset-bottom))' }}
      >
        <div className="m-auto flex flex-col items-center gap-5">
        <p className="text-[11px] font-bold uppercase" style={{ letterSpacing: '.14em', color: '#a9e8c9' }}>Session cartes</p>
        <h1 className="font-black text-2xl" style={{ fontFamily: "'Archivo', 'Inter', system-ui, sans-serif" }}>
          {session?.titre || 'Session QCM'}
        </h1>
        {/* Le total d'élèves n'est connu qu'à la première question (il vient
            du flux) : avant, on ne montre que le nombre de questions. */}
        <p className="text-sm" style={{ color: '#aab3a8' }}>
          {totalQuestions} question{totalQuestions > 1 ? 's' : ''}
          {total > 0 ? ` · ${total} élève${total > 1 ? 's' : ''}` : ''}
        </p>

        {/* Un seul geste : le téléphone relié par le QR sait que la projection
            est ouverte, c'est elle qui a fait le jeton. */}
        <button
          type="button"
          onClick={onStart}
          disabled={isTransitioning}
          className="flex items-center gap-2.5 px-8 py-4 rounded-2xl font-bold text-base text-white disabled:opacity-60"
          style={{ background: QCM_GREEN }}
        >
          <Play size={18} />
          Lancer le quizz
        </button>
        </div>
      </div>
    );
  }

  if (ecran === 'pilotage') {
    return (
      <PilotageFin
        titre={titrePilotage(deroule)}
        etape={etape}
        geste={geste}
        precedent={precedent}
        isTransitioning={isTransitioning}
        onEtape={onEtape}
      />
    );
  }

  if (ecran === 'fin') {
    return (
      <div className="fixed inset-0 z-50 flex flex-col items-center justify-center gap-5 px-8 text-center text-white" style={{ background: '#0e1116' }}>
        <CheckCircle size={44} style={{ color: QCM_GREEN }} />
        <h1 className="font-black text-2xl" style={{ fontFamily: "'Archivo', 'Inter', system-ui, sans-serif" }}>
          Session terminée !
        </h1>
        <p className="text-sm" style={{ color: '#aab3a8' }}>
          {/* Plus de podium ici : il a son étape à lui (D19), et l'écran projeté
              affiche « Session terminée » comme celui-ci. */}
          Les résultats sont disponibles depuis le tableau de bord.
        </p>
        <p className="text-sm" style={{ color: '#aab3a8' }}>Vous pouvez fermer cette page.</p>
      </div>
    );
  }

  // ── Passation en cours : scan plein écran ──
  return (
    <div ref={containerRef} className="fixed inset-0 z-50 bg-black overflow-hidden flex flex-col">
      <ScanViewport
        videoRef={videoRef}
        canvasRef={canvasRef}
        cameraActive={cameraActive}
        cameraError={cameraError}
        onRestart={restart}
        flash={flash}
        scanning={scanning}
      />

      <DetectionBadges badges={badges} />

      {/* Réponse révélée : la classe regarde la projection, la caméra est en
          pause, et le téléphone le dit. */}
      {revealed ? <BandeauCorrection bonneReponse={bonneReponse} reponses={reponses} /> : null}

      <ScanHeader
        numeroQuestion={numeroQuestion}
        sousTitre={revealed ? 'Correction projetée' : (scanOuvert ? 'Scan en cours' : 'Prêt à scanner')}
        done={done}
        total={total}
        torchSupported={torchSupported}
        torchOn={torchOn}
        onToggleTorch={toggleTorch}
        onOuvrirReponses={() => setReponsesOuvertes(true)}
        qualite={LIBELLES_ANALYSE[largeurAnalyse] || null}
        onChoisirTaille={() => setChoixTailleOuvert((o) => !o)}
      />

      {choixTailleOuvert ? (
        <ChoixTaille
          largeurForcee={largeurForcee}
          largeurAnalyse={largeurAnalyse}
          onChoisir={(largeur) => { setLargeurForcee(largeur); setChoixTailleOuvert(false); }}
        />
      ) : null}

      {/* Bas de l'écran : répartition A-D, « Scan ! », le bouton. Plus de
          pastilles des restants (2026-09-19) : sous jeton elles ne montraient
          que des numéros, le compteur de l'en-tête dit déjà 18/24, et elles
          étaient reconstruites à chaque analyse. */}
      <div className="relative z-[4] mt-auto pt-3" style={{ background: 'linear-gradient(transparent, rgba(0,0,0,.88) 55%)' }}>
        <RepartitionReponses reponses={reponses} bonneReponse={bonneReponse} />
        {erreurPilotage ? (
          <p role="alert" className="mx-4 mb-2 px-3 py-2 rounded-xl text-sm font-semibold text-white" style={{ background: 'rgba(220,38,38,.85)' }}>
            {erreurPilotage}
          </p>
        ) : null}
        <ScanFooter
          libelle={libelleSuivant}
          disabled={isTransitioning || (derniereQuestion && !doitReveler && !geste)}
          onSuivant={handleSuivant}
          onOuvrirSecours={() => setSecoursOuvert(true)}
          scanPossible={peutScanner && !scanOuvert}
          onScan={onOuvrirScan}
        />
      </div>

      <ReponsesSheet
        open={reponsesOuvertes}
        onClose={() => setReponsesOuvertes(false)}
        numeroQuestion={numeroQuestion}
        assignations={assignations}
        reponses={reponses}
      />

      <SecoursDrawer
        open={secoursOuvert}
        onClose={() => setSecoursOuvert(false)}
        numeroQuestion={numeroQuestion}
        restants={restants}
        onScan={onScan}
        disabled={ecran !== 'quiz' || questionActive === 0}
      />
    </div>
  );
};

export default ScanMobile;
